import { Engagement, PayeeEngagement } from "@wingspanhq/wingspan-api-sdk";

import { WorkLogEngagementOption } from "./selectorWorkLogEngagementOptions";

/**
 * A payee's assignments that can carry a work log, built from the first-class
 * collections rather than the search index.
 *
 * **The work-logging rule.** An assignment supports work logging when its
 * engagement template has work definitions attached — that is exactly what the
 * search projection computes into `PayeeRowEngagement.isWorkLoggingEnabled`
 * (`engagement?.workDefinitions?.length ? 'true' : 'false'`). `Engagement`
 * exposes the same thing as `workDefinitionIds`, so the rule is reproduced here
 * from the source of truth instead of read off the index — which also means this
 * keeps working when the search service does not.
 *
 * An engagement the payer cannot resolve is treated as not work-logging-capable:
 * without its template there is no basis to claim it is.
 *
 * **`engagementType` comes from the `Engagement`, not the `PayeeEngagement`.**
 * Verified against integration: `listPayeeEngagements` returns
 * `engagementType: undefined` even for a payee-engagement created with an
 * explicit type, so reading it there would make every assignment look like a
 * contractor and permanently hide the W-2 pay-period field. The engagement
 * template is the reliable source.
 *
 * Neither type nor status is filtered here. The create modal offers a pay
 * period only for `Employee` assignments and accepts the others without one, so
 * every live assignment belongs in the list and each caller applies its own
 * policy — the same split as `selectorWorkLogEngagementOptions`.
 */
export const selectorWorkLogPayeeEngagementOptions = ({
  payeeEngagements,
  engagements
}: {
  payeeEngagements: PayeeEngagement[];
  engagements: Engagement[];
}): WorkLogEngagementOption[] => {
  const workLoggingEngagements = new Map(
    engagements
      .filter(
        engagement => !!engagement.id && !!engagement.workDefinitionIds?.length
      )
      .map(engagement => [engagement.id as string, engagement])
  );

  return payeeEngagements
    .filter(
      payeeEngagement =>
        !!payeeEngagement.id &&
        !!payeeEngagement.engagementId &&
        workLoggingEngagements.has(payeeEngagement.engagementId)
    )
    .map(payeeEngagement => {
      const engagement = workLoggingEngagements.get(
        payeeEngagement.engagementId as string
      );

      return {
        value: payeeEngagement.id as string,
        label:
          payeeEngagement.engagementName || engagement?.name || "Assignment",
        engagementType:
          engagement?.engagementType ?? payeeEngagement.engagementType,
        status: payeeEngagement.status
      };
    });
};
